import { CollectionRuleResponse } from "@authory-packages/core";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { V3ToggleWithLabel } from "../ToggleSwitch";
import { VerticalSpacing } from "../VerticalSpacing";
import { RightAlignPanel } from "../ReusableStyledComponents/RightAlignPanel";
import { V3Button } from "../Button";
import { getKeywordRules, getSourceRules, getTypeRules } from "../../utils/collectionRulesParser";
import { useState } from "react";
import { ModalLoadingSpinner } from "../ReusableStyledComponents/ModalLoadingSpinner";
import { FORMATED_SOURCE_TYPES } from "../../utils/getFormattedSourceTypes";

const getRulesDescription = (label: string, value: string) => (
    <VerticalSpacing bottom={8}>
        <V3BodyGrey48>
            {label}: <strong>{value}</strong>
        </V3BodyGrey48>
    </VerticalSpacing>
);

export enum AutoUpdateFlow {
    Source = "Source",
    Item = "Item",
    BulkAdd = "BulkAdd"
}

export interface RulesAutoUpdateProps {
    target: AutoUpdateFlow;
    rules: CollectionRuleResponse[];
}

interface AutoUpdateScreenProps extends Omit<RulesAutoUpdateProps, "target"> {
    srcList: { label: string; value: string }[];
    onYesHandler: () => Promise<void>;
    onNoHandler: () => void;
}

export const AutoUpdateScreen = ({ rules, srcList, onYesHandler, onNoHandler }: AutoUpdateScreenProps) => {
    const sourceRules = getSourceRules(rules, srcList);
    const typeRules = getTypeRules(rules);
    const keywordRules = getKeywordRules(rules);
    const [modalSubmitting, setModalSubmitting] = useState(false);

    const hasSourceRules = !!sourceRules.length;
    const hasTypeRules = !!typeRules.length;
    const hasKeywordRules = !!keywordRules.length;

    const [autoUpdate, setAutoUpdate] = useState(true);

    return (
        <>
            <VerticalSpacing bottom={32}>
                {" "}
                <V3BodyGrey48>
                    Authory can automatically update this collection with future content. Do you want to add the following auto-update rule? You can
                    change this later anytime.
                </V3BodyGrey48>
            </VerticalSpacing>
            <VerticalSpacing bottom={12}>
                <V3ToggleWithLabel name="add-rules" onChange={() => setAutoUpdate(!autoUpdate)} label="Add auto-update rule" checked={autoUpdate} />
            </VerticalSpacing>
            <VerticalSpacing bottom={12}>
                <V3BodyGrey48>Automatically add future content matching these conditions:</V3BodyGrey48>
            </VerticalSpacing>
            {hasSourceRules &&
                getRulesDescription("Source", sourceRules.map((srcName) => srcList.find((src) => src.value === srcName)?.label).join(", "))}
            {hasTypeRules &&
                getRulesDescription("Type", typeRules.map((tp) => FORMATED_SOURCE_TYPES.find((frmt) => frmt.value === tp)?.text).join(", "))}
            {hasKeywordRules && getRulesDescription("Keyword", keywordRules.join(", "))}
            <VerticalSpacing top={45}>
                <RightAlignPanel>
                    {modalSubmitting ? (
                        <ModalLoadingSpinner />
                    ) : (
                        <>
                            <div>
                                <V3Button
                                    text={"Continue"}
                                    onClick={async () => {
                                        if (autoUpdate) {
                                            try {
                                                setModalSubmitting(true);
                                                await onYesHandler();
                                                setModalSubmitting(false);
                                            } catch {
                                                setModalSubmitting(false);
                                            }
                                        } else {
                                            onNoHandler();
                                        }
                                    }}
                                    autoWidth
                                />
                            </div>
                        </>
                    )}
                </RightAlignPanel>
            </VerticalSpacing>
        </>
    );
};
